import React, { useCallback, useEffect, useState } from 'react';
import { HardDrive, RefreshCw } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import { DARK, INGEST_STATUS_META, btnStyle, formatTimestamp } from './theme';
import JobDetailView from './JobDetailView';
import ProxiesOnlyAction from './ProxiesOnlyAction';
import LiveJobMiniProgress from './progress/LiveJobMiniProgress';

// Each ingest already generates a jobId (used for progress events during the
// run) -- this view is a persisted list of those, so past ingests stay
// visible (with their Job ID, status, and file counts) after closing or
// reopening the window, the same way the proxy queue's own history does.
export default function IngestionRecordsList({
  c, devices, matchedProfile, runAutomationOnDevice, onManualAssign,
  destinationPresets, remoteConnections, onSelectRemoteSource,
  liveJobId, liveProgress, liveProgressPct, refreshTrigger,
}) {
  const isDarkMode = c === DARK;
  const [jobs, setJobs] = useState(null);
  const [openJobId, setOpenJobId] = useState(null);
  const [proxiesOnlyOpenId, setProxiesOnlyOpenId] = useState(null);
  const [showRemotePicker, setShowRemotePicker] = useState(false);
  const [proxyQueueSummary, setProxyQueueSummary] = useState(null);
  const ingestTableColumns = '1.15fr 0.9fr 2.2fr 0.75fr 1fr';

  const loadJobs = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.listJobs();
    if (result?.success) setJobs(result.data);
  }, []);
  // refreshTrigger has no meaning of its own -- it's just a counter the
  // parent bumps right after creating a job via the New Ingest form, so a
  // newly created job shows up immediately instead of waiting for the next
  // 4s poll below (see CardIngestWindow's bumpJobsRefresh).
  useEffect(() => { loadJobs(); }, [loadJobs, refreshTrigger]);
  // Re-poll periodically so a queued row that starts running (or a running
  // row that completes) in a background tick still updates this list even
  // when the interactive events below don't happen to fire for it.
  useEffect(() => {
    const timer = setInterval(loadJobs, 4000);
    return () => clearInterval(timer);
  }, [loadJobs]);

  // Proxy engine backlog depth -- was never surfaced anywhere in the UI, so
  // a new "proxy only" request could sit behind a very deep queue (real
  // backlogs run into the tens of thousands of jobs) with zero indication
  // why nothing seemed to be happening. Cheap counts-only query, safe to
  // poll on the same cadence as the job list above.
  const loadProxyQueueSummary = useCallback(async () => {
    const result = await window.electronAPI.proxyQueue.getQueueSummary();
    if (result?.success) setProxyQueueSummary(result.data);
  }, []);
  useEffect(() => { loadProxyQueueSummary(); }, [loadProxyQueueSummary]);
  useEffect(() => {
    const timer = setInterval(loadProxyQueueSummary, 4000);
    return () => clearInterval(timer);
  }, [loadProxyQueueSummary]);

  const cancelAllQueuedProxies = useCallback(async () => {
    if (!window.confirm(`Cancel all ${proxyQueueSummary?.queued.toLocaleString() || ''} queued proxy jobs? Anything already running finishes; nothing already copied/proxied is affected.`)) return;
    await window.electronAPI.proxyQueue.cancelAllQueued();
    loadProxyQueueSummary();
  }, [proxyQueueSummary, loadProxyQueueSummary]);

  useEffect(() => {
    const offDetected = window.electronAPI.cardIngest.onDetected(loadJobs);
    const offMatched = window.electronAPI.cardIngest.onProfileMatched(loadJobs);
    const offComplete = window.electronAPI.cardIngest.onComplete(loadJobs);
    return () => { offDetected?.(); offMatched?.(); offComplete?.(); };
  }, [loadJobs]);

  const openReport = useCallback(async (jobId, e) => {
    e.stopPropagation();
    const result = await window.electronAPI.cardIngest.openJobReport(jobId);
    if (!result?.success) console.error('[Ingest] Failed to open report:', result?.error);
  }, []);

  const deleteJob = useCallback(async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this ingest record? The footage will not be deleted.')) return;
    const result = await window.electronAPI.cardIngest.deleteJob(jobId);
    if (result?.success) loadJobs();
  }, [loadJobs]);

  const clearJobs = useCallback(async () => {
    if (!window.confirm('Clear non-running ingest history? Footage and reports will stay on disk.')) return;
    const result = await window.electronAPI.cardIngest.clearJobs();
    if (result?.success) loadJobs();
  }, [loadJobs]);

  // Cancels a queued or running ingest record -- for proxy-only jobs
  // (tracked entirely via proxyQueue, never in cardIngestManager.activeJobs)
  // cancelOffload alone wouldn't stop the underlying transcodes still
  // running/queued in the proxy engine, so their proxyJobIds are cancelled
  // there directly first. cancelOffload then marks the ingest record itself
  // cancelled either way (handles both an active copy and a not-yet-started
  // queued one -- see its own docstring in card-ingest.js).
  const cancelIngestJob = useCallback(async (job, e) => {
    e.stopPropagation();
    if (!window.confirm('Cancel this ingest? Files already copied/proxied so far are kept.')) return;
    if (job.proxyJobIds?.length) {
      await window.electronAPI.proxyQueue.cancelJobs(job.proxyJobIds);
    }
    await window.electronAPI.cardIngest.cancelOffload(job.jobId);
    loadJobs();
  }, [loadJobs]);

  const selectedJob = jobs?.find(job => job.jobId === openJobId) || null;
  if (selectedJob) {
    const meta = INGEST_STATUS_META[selectedJob.status] || { label: selectedJob.status, color: 'muted' };
    return (
      <div style={{ width: '100%' }}>
        <button onClick={() => setOpenJobId(null)} style={{ ...btnStyle(c, 'ghost'), marginBottom: '12px' }}>
          ← Back to ingest queue
        </button>
        <div style={{ border: `1px solid ${c.rule}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '12px', background: c.subtleBg, borderBottom: `1px solid ${c.rule}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
              <strong>{selectedJob.deviceLabel || selectedJob.cameraProfile?.family || 'Ingest details'}</strong>
              <span style={{ color: c[meta.color], fontWeight: 600 }}>{meta.label}</span>
            </div>
            <div style={{ marginTop: '6px', color: c.muted, fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
              {selectedJob.sourcePath || '—'} → {selectedJob.destPath || 'Not assigned'}
            </div>
          </div>
          <JobDetailView c={c} job={selectedJob} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <strong style={{ fontSize: '12px' }}>Ingest queue and history</strong>
        {!!jobs?.length && <button onClick={clearJobs} style={btnStyle(c, 'ghost')}>Clear history</button>}
      </div>
      {proxyQueueSummary && (proxyQueueSummary.queued > 0 || proxyQueueSummary.running > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: c.muted, marginBottom: '10px' }}>
          <span>
            Proxy engine: {proxyQueueSummary.queued.toLocaleString()} waiting · {proxyQueueSummary.running} running
            {proxyQueueSummary.failed > 0 ? ` · ${proxyQueueSummary.failed.toLocaleString()} failed` : ''}
            {proxyQueueSummary.queued > 200 && ' — a new proxy request joins the back of this line, oldest first'}
          </span>
          {proxyQueueSummary.queued > 0 && (
            <button onClick={cancelAllQueuedProxies} style={btnStyle(c, 'ghost')}>Cancel all queued</button>
          )}
        </div>
      )}
      {remoteConnections.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          {!showRemotePicker ? (
            <button onClick={() => setShowRemotePicker(true)} style={btnStyle(c, 'ghost')}>
              <RefreshCw size={13} /> Pull from a remote server…
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <PillDropdown
                  value=""
                  onChange={(v) => {
                    const conn = remoteConnections.find(c2 => c2.id === v);
                    setShowRemotePicker(false);
                    if (conn) onSelectRemoteSource(conn);
                  }}
                  isDarkMode={isDarkMode}
                  options={remoteConnections.map(conn => ({ value: conn.id, label: `${conn.name} (${conn.protocol}://${conn.host})` }))}
                  placeholder="Pull from which server?"
                  triggerWidth="100%"
                />
              </div>
              <button onClick={() => setShowRemotePicker(false)} style={btnStyle(c, 'ghost')}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {jobs === null && <div style={{ fontSize: '12px', color: c.muted }}>Loading…</div>}
      {jobs && jobs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: c.muted, fontSize: '13px' }}>
          <HardDrive size={28} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>No ingest records yet.</p>
          <p style={{ fontSize: '11px', marginTop: '6px' }}>
            Insert a card, or use New ingest above to choose any folder or hard disk.
          </p>
        </div>
      )}

      <div
        role="table"
        aria-label="Ingest records"
        style={{ width: '100%', overflowX: 'auto', border: jobs?.length ? `1px solid ${c.rule}` : 'none', borderRadius: '8px' }}
      >
        {jobs?.length > 0 && (
          <div role="row" style={{
            minWidth: '880px', display: 'grid',
            gridTemplateColumns: ingestTableColumns,
            gap: '12px', padding: '8px 10px', background: c.subtleBg,
            borderBottom: `1px solid ${c.rule}`, color: c.muted,
            fontSize: '10.5px', fontWeight: 600,
          }}>
            <span role="columnheader">Card / camera</span>
            <span role="columnheader">Date</span>
            <span role="columnheader">Source → destination</span>
            <span role="columnheader">Files</span>
            <span role="columnheader">Status</span>
          </div>
        )}
        {jobs && jobs.map((job) => {
          const meta = INGEST_STATUS_META[job.status] || { label: job.status, color: 'muted' };
          const open = openJobId === job.jobId;
          const device = devices.find(d => d.jobId === job.jobId);
          const isMatched = matchedProfile?.drive?.jobId === job.jobId ? matchedProfile.profile : null;
          const isLive = job.status === 'running' && liveJobId === job.jobId;
          const canExpand = true;
          return (
            <div key={job.jobId} style={{
              minWidth: '880px', borderBottom: `1px solid ${c.rule}`,
              boxShadow: isMatched ? `inset 3px 0 0 ${c.accent}` : 'none',
              fontSize: '11px', overflow: 'hidden',
            }}>
              <div
                role="row"
                tabIndex={canExpand ? 0 : undefined}
                aria-expanded={canExpand ? open : undefined}
                aria-controls={canExpand ? `ingest-details-${job.jobId}` : undefined}
                aria-label={canExpand ? `${job.deviceLabel || job.cameraProfile?.family || 'Unknown'} ingest; ${open ? 'collapse' : 'expand'} details` : undefined}
                onClick={() => canExpand && setOpenJobId(open ? null : job.jobId)}
                onKeyDown={(e) => {
                  if (e.currentTarget === e.target && canExpand && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setOpenJobId(open ? null : job.jobId);
                  }
                }}
                style={{ padding: '8px 10px', cursor: canExpand ? 'pointer' : 'default' }}
              >
                <div style={{
                  display: 'grid', gridTemplateColumns: ingestTableColumns,
                  gap: '12px', alignItems: 'start',
                }}>
                  <div role="cell" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.deviceLabel || job.cameraProfile?.family || 'Unknown'}
                    </div>
                    <div style={{ color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.cameraProfile?.family || '—'}{job.profileName ? ` · ${job.profileName}` : ''}
                    </div>
                  </div>
                  <div role="cell">
                    <div>{formatTimestamp(job.startedAt)}</div>
                    {job.finishedAt && <div style={{ color: c.muted }}>{formatTimestamp(job.finishedAt)}</div>}
                  </div>
                  <div role="cell" style={{ minWidth: 0, color: c.muted, fontFamily: 'monospace' }}>
                    <div title={job.sourcePath || ''} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.sourcePath || '—'}
                    </div>
                    <div title={job.destPath || ''} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      → {job.destPath || 'Not assigned'}
                    </div>
                  </div>
                  <div role="cell">
                    {job.status === 'queued' ? '—' : `${job.filesProcessed}/${job.fileCount}`}
                    {job.failedCount > 0 && <div style={{ color: c.err }}>{job.failedCount} failed</div>}
                  </div>
                  <div role="cell" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: c[meta.color] }}>{meta.label}</div>
                    {(isLive || job.status === 'running') && (
                      <LiveJobMiniProgress
                        c={c}
                        pct={isLive ? liveProgressPct : (job.progressPercent || 0)}
                        activeFiles={isLive ? liveProgress?.activeFiles : undefined}
                      />
                    )}
                  </div>
                </div>

                {job.status === 'queued' && (
                  <div style={{ marginTop: '7px', paddingTop: '7px', borderTop: `1px solid ${c.rule}`, display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    {isMatched ? (
                      <>
                        <span style={{ flex: 1, color: c.muted, alignSelf: 'center' }}>
                          Matches automation "{isMatched.name}" → {isMatched.projectName}
                        </span>
                        {device && <button onClick={() => runAutomationOnDevice(device, isMatched)} style={btnStyle(c, 'primary')}>Run</button>}
                      </>
                    ) : device ? (
                      <>
                        {destinationPresets.length > 0 && (
                          <div style={{ flex: 1 }}>
                            <PillDropdown
                              value=""
                              onChange={(v) => {
                                const preset = destinationPresets.find(d => d.id === v);
                                if (preset) runAutomationOnDevice(device, { name: preset.name, destinationId: preset.id });
                              }}
                              isDarkMode={isDarkMode}
                              options={destinationPresets.map(d => ({ value: d.id, label: d.name }))}
                              placeholder="Assign a saved destination…"
                              triggerWidth="100%"
                            />
                          </div>
                        )}
                        <button onClick={() => onManualAssign(device)} style={btnStyle(c, 'default')}>Manual setup…</button>
                        <button onClick={() => setProxiesOnlyOpenId(proxiesOnlyOpenId === job.jobId ? null : job.jobId)} style={btnStyle(c, 'ghost')}>
                          Proxies only…
                        </button>
                      </>
                    ) : (
                      <span style={{ flex: 1, color: c.muted, alignSelf: 'center' }}>Source is currently unavailable</span>
                    )}
                    <button onClick={(e) => cancelIngestJob(job, e)} style={btnStyle(c, 'ghost')}>Cancel</button>
                  </div>
                )}

                {job.status === 'running' && (
                  <div style={{ marginTop: '7px', paddingTop: '7px', borderTop: `1px solid ${c.rule}` }} onClick={e => e.stopPropagation()}>
                    <button onClick={(e) => cancelIngestJob(job, e)} style={btnStyle(c, 'ghost')}>Cancel</button>
                  </div>
                )}

                {job.status === 'queued' && device && proxiesOnlyOpenId === job.jobId && (
                  <div onClick={e => e.stopPropagation()}>
                    <ProxiesOnlyAction c={c} device={device} />
                  </div>
                )}

                {['completed', 'completed_with_errors', 'failed', 'cancelled', 'interrupted'].includes(job.status) && (
                  <>
                    <button onClick={(e) => openReport(job.jobId, e)} style={{ ...btnStyle(c, 'ghost'), marginTop: '7px' }}>View report</button>
                    <button onClick={(e) => deleteJob(job.jobId, e)} style={{ ...btnStyle(c, 'ghost'), marginTop: '7px' }}>Remove record</button>
                  </>
                )}
              </div>
              {open && (
                <div id={`ingest-details-${job.jobId}`} role="region" aria-label="Ingest file details">
                  <JobDetailView c={c} job={job} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
